import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';

interface Props {
  sectionKey: string;
  value: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void | Promise<void>;
  submitting?: boolean;
  onCancel?: () => void;
}

/**
 * Edit a single about-content row. Renders a generic JSON editor — content
 * varies per key but the contract is the same JSONB shape.
 */
export function AboutContentEditForm({ sectionKey, value, onChange, submitting, onCancel }: Props) {
  const [text, setText] = useState<string>(JSON.stringify(value ?? {}, null, 2));
  const [error, setError] = useState<string | null>(null);

  function commit() {
    try {
      const parsed = JSON.parse(text) as Record<string, unknown>;
      onChange(parsed);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    commit();
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <Field id={`${sectionKey}.hint`} label={`Editing ${sectionKey}`} hint="JSON shape varies per key. See vonlof_docs/16-ADMIN_DATA_MODEL.md for schemas.">
        <span />
      </Field>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        rows={20}
        className="w-full rounded-md border border-border bg-bg p-3 font-mono text-xs text-fg focus:border-gold-500/60 focus:outline-none focus:ring-2 focus:ring-gold-500/20"
      />
      {error && (
        <p className="text-xs text-danger" role="alert">
          {error}
        </p>
      )}
      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" loading={submitting} onClick={commit}>
          Save changes
        </Button>
      </div>
    </form>
  );
}